
import { useState, useCallback, useEffect } from 'react';
import { Trip, ItineraryEvent } from '@/types/itinerary';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Hook to fetch and manage transportation events from the database
 * Shows ALL transportation events from all users in the trip
 */
export const useTransportationEvents = (trip: Trip | null) => {
  const [transportationEvents, setTransportationEvents] = useState<ItineraryEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const fetchTransportationEvents = useCallback(async () => {
    if (!trip?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if we're online
      if (!navigator.onLine) {
        throw new Error("You're currently offline. Please check your internet connection.");
      }
      
      console.log("Fetching transportation events for trip:", trip.id);
      
      const { data, error } = await supabase
        .from('itinerary_events')
        .select('*')
        .eq('trip_id', trip.id)
        .eq('is_transportation', true)
        .order('date', { ascending: true })
        .order('time', { ascending: true });
        
      if (error) {
        throw error;
      }
      
      console.log("Transportation events fetched:", data);
      
      // Process the events - notice we're not filtering by user
      const processedEvents = data?.map(event => ({
        ...event,
        isTransportation: true,
        transportationType: event.transportation_type || '',
      })) || [];
      
      setTransportationEvents(processedEvents);
      
      // Cache events if we have them
      if (processedEvents.length > 0) {
        localStorage.setItem('cachedTransportationEvents', JSON.stringify(processedEvents));
      }
    } catch (error: any) {
      console.error('Error fetching transportation events:', error);
      
      let errorMessage = "Failed to load transportation schedule";
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setError(errorMessage);
      
      // Use cached data if available
      const cachedEvents = localStorage.getItem('cachedTransportationEvents');
      if (cachedEvents) {
        try {
          const parsedEvents = JSON.parse(cachedEvents);
          if (Array.isArray(parsedEvents) && parsedEvents.length > 0) {
            setTransportationEvents(parsedEvents);
            toast.info("Showing cached transportation data");
          }
        } catch (e) {
          console.error("Error parsing cached events", e);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [trip?.id]);
  
  // Add useEffect to automatically fetch data when the trip is available
  useEffect(() => {
    if (trip?.id) {
      fetchTransportationEvents();
    }
  }, [trip?.id, fetchTransportationEvents]);
  
  // Clear the error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  return {
    transportationEvents,
    isLoading,
    error,
    fetchTransportationEvents,
    clearError
  };
};
